import { useEffect, useState } from 'react';
import { CARE_STAFF_ROLES, ROLE_LABEL, type ApiClient, type Role } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';
import { useReadOnly } from '../../ui/ReadOnly';

/** The design's three Residents personas. Partner and Super Admin see the Operator's page. */
export type ResidentsPersona = 'Operator' | 'Facility Admin' | 'Care Staff';

export function personaOf(role: Role | null): ResidentsPersona | null {
  if (!role) return null;
  if (role === 'ADMIN') return 'Facility Admin';
  if (CARE_STAFF_ROLES.has(role)) return 'Care Staff';
  if (role === 'SUPER_ADMIN' || role === 'OPERATOR' || role === 'PARTNER') return 'Operator';
  return null;
}

export interface ResidentsCtx {
  persona: ResidentsPersona;
  /** The role on screen (View as, or the signed-in role). */
  role: Role | null;
  /** The name the design prints for the role ("Read-only as Super Admin."): its V9 tier name. */
  roleLabel: string;
  /** Writes are off: Partner, View as, or Aithora (the watch list is read-only for Aithora). */
  readOnly: boolean;
  /**
   * May act on resident care (V9 window.__aiFacAct): the building's own staff only. Super Admin,
   * Support (OPERATOR) and the Owner/Operator (PARTNER) read every building and act in none.
   */
  canAct: boolean;
  viewerId: string | null;
  orgId: string | null;
  client: ApiClient;
}

/**
 * The V9 design's name for a read-only seat ("Read-only as Support"): the Aithora tiers are
 * Super Admin and Support (the stored role OPERATOR), and the design's Owner/Operator is the
 * stored role PARTNER. Every other role keeps its own label.
 */
export function tierLabel(role: Role | null): string {
  if (role === 'SUPER_ADMIN') return 'Super Admin';
  if (role === 'OPERATOR') return 'Support';
  if (role === 'PARTNER') return 'Owner/Operator';
  return role ? ROLE_LABEL[role] : 'your role';
}

/** SAMPLE, MODELED and Example labels show to Super Admin and Dev only (V9 no-sample.js). */
export function seesSampleLabels(role: Role | null): boolean {
  return role === 'SUPER_ADMIN' || role === 'DEVELOPER';
}

export function useResidentsCtx(): ResidentsCtx | null {
  const { role } = useConsoleRole();
  const { session, client } = useSession();
  const ro = useReadOnly();
  const persona = personaOf(role);
  if (!persona) return null;
  return {
    persona,
    role,
    // The real role's name (Super Admin is not "Operator"); persona only picks the layout.
    roleLabel: tierLabel(role),
    readOnly: ro,
    canAct: !ro && persona !== 'Operator',
    viewerId: session?.user.id ?? null,
    orgId: session?.user.organizationId ?? null,
    client,
  };
}

/** Load one route with the session's client. */
export function useApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], opts: { enabled?: boolean; pollMs?: number } = {}): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], opts);
}

/** The console's width (the design's shellW) and its phone frame (< 760). */
export function useWidth(): { width: number; phone: boolean } {
  const read = () => (typeof window === 'undefined' ? 1440 : window.innerWidth);
  const [width, setWidth] = useState<number>(read);
  useEffect(() => {
    const on = () => setWidth(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return { width, phone: width < 760 };
}

/** One clock for the page, so every "ago" agrees. */
export function useNow(): number {
  const [now] = useState(() => Date.now());
  return now;
}
